/* ======================================================================
   logo-mockup.js  (DEDICATED THEME ASSET)
   ----------------------------------------------------------------------
   PURPOSE
   - Adds a "Create my mockup" modal editor on product pages (Konva.js)
   - Lets customer place/scale a logo on a selected product image
   - Generates a rendered mockup PNG, uploads logo+mockup to Cloudflare Worker/R2
   - Adds to cart with line item properties: _mockup_url, _logo_url, _mockup_transform

   FEATURES
   - Background image selector (thumbnails)
   - Constrained placement inside an admin-defined zone (metafield JSON)
   - Admin/Design-mode zone editor (drag/resize zone + copy JSON)
   - Local background removal (NO external deps, NO AI) for PNG/JPG only + sensitivity slider
   - Allowed uploads: AI / EPS / SVG / JPG / PNG
     * Preview supported: SVG/JPG/PNG
     * Remove background supported: JPG/PNG only
     * AI/EPS: accepted in chooser but not previewable; shows help "How to export AI/EPS?"
   - FR/EN auto: English when URL contains "/en/", else French

   REQUIRED LIQUID "ROOT" ON PRODUCT PAGE
   <div id="logo-mockup-root"
        data-variant-id="..."
        data-api-base="https://YOUR-WORKER.example.com"
        data-dpo-selector="input[name='properties[Logo]']"
        data-zones='{"version":1,"zone":{"x":0.25,"y":0.25,"w":0.5,"h":0.5}}'
        data-zone-editor="true|false"   (true only in design mode recommended)
        data-images='[{"id":"...","src":"...","thumb":"..."}, ...]'>
     <button id="logo-mockup-open" type="button">...</button>
   </div>

   REQUIRED SCRIPTS (in theme.liquid, product pages)
   - Konva CDN: https://unpkg.com/konva@9/konva.min.js
   - This file: {{ 'logo-mockup.js' | asset_url }}

   CLOUDFLARE WORKER ENDPOINTS (minimal)
   - POST  {apiBase}/upload   (multipart form: file, kind=logo|mockup) -> { url, key }
   - GET   {apiBase}/file/<key>

   ====================================================================== */

(() => {
  /* =========================
     BEGIN YOURAPP: Guards
     ========================= */
  const root = document.getElementById("logo-mockup-root");
  if (!root) return;

  if (!window.Konva) {
    // Konva must be loaded. We silently return to avoid breaking the page.
    return;
  }
  /* =======================
     END YOURAPP: Guards
     ======================= */

  /* =========================
     BEGIN YOURAPP: i18n (/en/)
     ========================= */
  const isEN = window.location.pathname.includes("/en/");
  const I18N = {
    fr: {
      title: "Mockup",
      uploadHint: "Importez votre logo (AI/EPS/SVG/PNG/JPG).",
      loadingImg: "Chargement image…",
      loadingLogo: "Chargement logo…",
      changeImg: "Changement d’image…",
      removeBg: "Supprimer le fond",
      removingBg: "Suppression du fond…",
      removedBgOk: "Fond supprimé ✅",
      addToCart: "Ajouter au panier",
      rendering: "Rendu mockup…",
      uploading: "Upload…",
      adding: "Ajout au panier…",
      errBg: "Échec suppression de fond.",
      errAdd: "Erreur lors de l’ajout au panier.",
      needLogo: "Ajoutez un logo d’abord.",
      previewRequired: "Un aperçu est nécessaire. Merci d’envoyer un SVG/PNG/JPG pour générer le mockup.",
      zoneEdit: "Éditer la zone (admin)",
      copyZone: "Copier JSON zone",
      copied: "JSON copié ✅",
      manualCopy: "Copie manuelle : Ctrl/Cmd+C",
      bgSensitivity: "Sensibilité du fond",
      bgSensitivityHelp: "Plus élevé = supprime plus (risque d’attaquer le logo).",
      aiEpsNotPreview: "Les fichiers AI/EPS doivent être exportés en SVG/PNG/JPG pour l’aperçu.",
      svgNoBg: "La suppression de fond fonctionne uniquement pour PNG/JPG. Pour un SVG, exportez en PNG/JPG.",
      unsupported: "Format non supporté. Merci d’envoyer AI/EPS/SVG/PNG/JPG.",
      aiEpsHelpLink: "Comment exporter un AI/EPS ?",
      aiEpsHelpTitle: "Exporter un fichier AI/EPS pour l’aperçu",
      aiEpsHelpBody: [
        "Pour générer un mockup sur le site, exportez votre fichier AI/EPS en SVG ou PNG.",
        "",
        "Option 1 — Adobe Illustrator :",
        "• Fichier → Exporter → Exporter sous…",
        "• Choisir : SVG (recommandé) ou PNG",
        "• PNG : fond transparent si possible",
        "",
        "Option 2 — Autre logiciel (Inkscape / Affinity) :",
        "• Ouvrir le fichier puis exporter en SVG/PNG",
        "",
        "Astuce : si le logo a un fond blanc, exportez en PNG puis utilisez “Supprimer le fond”."
      ].join("\n")
    },
    en: {
      title: "Mockup",
      uploadHint: "Upload your logo (AI/EPS/SVG/PNG/JPG).",
      loadingImg: "Loading image…",
      loadingLogo: "Loading logo…",
      changeImg: "Switching image…",
      removeBg: "Remove background",
      removingBg: "Removing background…",
      removedBgOk: "Background removed ✅",
      addToCart: "Add to cart",
      rendering: "Rendering mockup…",
      uploading: "Uploading…",
      adding: "Adding to cart…",
      errBg: "Background removal failed.",
      errAdd: "Error while adding to cart.",
      needLogo: "Please add a logo first.",
      previewRequired: "Preview is required. Please upload SVG/PNG/JPG to generate a mockup.",
      zoneEdit: "Edit zone (admin)",
      copyZone: "Copy zone JSON",
      copied: "JSON copied ✅",
      manualCopy: "Manual copy: Ctrl/Cmd+C",
      bgSensitivity: "Background sensitivity",
      bgSensitivityHelp: "Higher = removes more (may eat into the logo).",
      aiEpsNotPreview: "AI/EPS files must be exported to SVG/PNG/JPG for preview.",
      svgNoBg: "Background removal works for PNG/JPG only. For SVG, export to PNG/JPG.",
      unsupported: "Unsupported format. Please upload AI/EPS/SVG/PNG/JPG.",
      aiEpsHelpLink: "How to export AI/EPS?",
      aiEpsHelpTitle: "Export an AI/EPS file for preview",
      aiEpsHelpBody: [
        "To generate a mockup on the site, please export your AI/EPS file to SVG or PNG.",
        "",
        "Option 1 — Adobe Illustrator:",
        "• File → Export → Export As…",
        "• Choose: SVG (recommended) or PNG",
        "• PNG: use a transparent background if possible",
        "",
        "Option 2 — Other software (Inkscape / Affinity):",
        "• Open the file, then export to SVG/PNG",
        "",
        "Tip: if your logo has a white background, export as PNG and use “Remove background”."
      ].join("\n")
    }
  };
  const t = (key) => (isEN ? I18N.en : I18N.fr)[key] || key;
  /* =======================
     END YOURAPP: i18n (/en/)
     ======================= */

  /* =========================
     BEGIN YOURAPP: Read config from root
     ========================= */
  function normalizeApiBase(value) {
    const v = (value || "").trim();
    if (!v) return "";
    if (v.startsWith("http://") || v.startsWith("https://")) return v;
    return `https://${v}`;
  }

  const apiBase = normalizeApiBase(root.dataset.apiBase);
  const variantId = (root.dataset.variantId || "").trim();
  const dpoSelector = root.dataset.dpoSelector || "input[name='properties[Logo]']";
  const zoneEditorAllowed =
    root.dataset.zoneEditor === "true" ||
    (window.Shopify && window.Shopify.designMode === true);

  function safeJson(s) {
    try { return JSON.parse(s); } catch { return null; }
  }

  const images = safeJson(root.dataset.images || "[]") || [];
  const zonesCfg = safeJson(root.dataset.zones || "null");
  const zoneRelDefault = zonesCfg?.zone || { x: 0.25, y: 0.25, w: 0.5, h: 0.5 };
  /* =======================
     END YOURAPP: Read config from root
     ======================= */

  /* =========================
     BEGIN YOURAPP: File type helpers (AI/EPS/SVG/PNG/JPG)
     ========================= */
  function extOf(name = "") {
    const m = name.toLowerCase().match(/\.([a-z0-9]+)$/);
    return m ? m[1] : "";
  }
  function isRasterExt(ext) {
    return ext === "png" || ext === "jpg" || ext === "jpeg";
  }
  function isSvgExt(ext) {
    return ext === "svg";
  }
  function isVectorPrintExt(ext) {
    return ext === "ai" || ext === "eps";
  }
  /* =======================
     END YOURAPP: File type helpers
     ======================= */

  /* =========================
     BEGIN YOURAPP: Local background removal (no AI, no external deps)
     - Flood fill from corners
     - Tolerance slider controls RGB distance
     ========================= */
  function rgbDist(a, b) {
    const dr = a[0] - b[0];
    const dg = a[1] - b[1];
    const db = a[2] - b[2];
    return Math.sqrt(dr * dr + dg * dg + db * db);
  }

  function setAlpha(data, idx, alpha) {
    data[idx + 3] = alpha;
  }

  function sampleAvgColor(imgData, x, y, radius = 2) {
    const { data, width, height } = imgData;
    let r = 0, g = 0, b = 0, n = 0;
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const xx = Math.max(0, Math.min(width - 1, x + dx));
        const yy = Math.max(0, Math.min(height - 1, y + dy));
        const i = (yy * width + xx) * 4;
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
        n++;
      }
    }
    return [Math.round(r / n), Math.round(g / n), Math.round(b / n)];
  }

  async function removeBackgroundLocal(file, opts = {}) {
    const {
      tolerance = 30,
      feather = 1,
      maxSize = 1600
    } = opts;

    const blobUrl = URL.createObjectURL(file);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = blobUrl;
    await img.decode();

    const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
    const W = Math.max(1, Math.round(img.width * scale));
    const H = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, W, H);
    URL.revokeObjectURL(blobUrl);

    const imgData = ctx.getImageData(0, 0, W, H);
    const data = imgData.data;

    const bgs = [
      sampleAvgColor(imgData, 0, 0),
      sampleAvgColor(imgData, W - 1, 0),
      sampleAvgColor(imgData, 0, H - 1),
      sampleAvgColor(imgData, W - 1, H - 1)
    ];

    const visited = new Uint8Array(W * H);
    const queue = new Int32Array(W * H);
    let qh = 0, qt = 0;

    function push(x, y) {
      const p = y * W + x;
      if (visited[p]) return;
      visited[p] = 1;
      queue[qt++] = p;
    }

    function isBgLike(x, y) {
      const i = (y * W + x) * 4;
      const px = [data[i], data[i + 1], data[i + 2]];
      for (const bg of bgs) {
        if (rgbDist(px, bg) <= tolerance) return true;
      }
      return false;
    }

    push(0, 0);
    push(W - 1, 0);
    push(0, H - 1);
    push(W - 1, H - 1);

    while (qh < qt) {
      const p = queue[qh++];
      const x = p % W;
      const y = (p / W) | 0;

      if (!isBgLike(x, y)) continue;

      const i = p * 4;
      setAlpha(data, i, 0);

      if (x > 0) push(x - 1, y);
      if (x < W - 1) push(x + 1, y);
      if (y > 0) push(x, y - 1);
      if (y < H - 1) push(x, y + 1);
    }

    if (feather > 0) {
      const copy = new Uint8ClampedArray(data);
      const rad = Math.min(3, Math.max(1, feather | 0));
      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          const p = y * W + x;
          const i = p * 4;
          if (copy[i + 3] !== 0) continue;

          for (let dy = -rad; dy <= rad; dy++) {
            for (let dx = -rad; dx <= rad; dx++) {
              const xx = x + dx, yy = y + dy;
              if (xx < 0 || xx >= W || yy < 0 || yy >= H) continue;
              const j = (yy * W + xx) * 4;
              if (data[j + 3] > 0) data[j + 3] = Math.min(data[j + 3], 220);
            }
          }
        }
      }
    }

    ctx.putImageData(imgData, 0, 0);

    const outBlob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 1));
    if (!outBlob) throw new Error("toBlob failed");
    return outBlob;
  }
  /* =======================
     END YOURAPP: Local background removal
     ======================= */

  /* =========================
     BEGIN YOURAPP: Networking helpers (Worker upload) + cart add
     ========================= */
  function fileFromBlob(blob, name) {
    return new File([blob], name, { type: blob.type || "application/octet-stream" });
  }

  async function uploadToWorker(file, kind) {
    if (!apiBase) throw new Error("Missing apiBase");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);

    const r = await fetch(`${apiBase}/upload`, { method: "POST", body: fd });
    if (!r.ok) throw new Error("upload failed");
    return await r.json();
  }

  async function addToCartWithProps(props) {
    const r = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: Number(variantId),
        quantity: 1,
        properties: props
      })
    });
    if (!r.ok) throw new Error("add to cart failed");
    return await r.json();
  }
  /* =======================
     END YOURAPP: Networking helpers
     ======================= */

  /* =========================
     BEGIN YOURAPP: Image loading helpers
     ========================= */
  async function loadImage(url) {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;
    await img.decode();
    return img;
  }

  async function fetchBlob(url) {
    const r = await fetch(url, { mode: "cors" });
    if (!r.ok) throw new Error("fetch failed");
    return await r.blob();
  }

  async function svgFileToHTMLImage(svgFile) {
    const svgText = await svgFile.text();
    const safeText = svgText.includes("xmlns=")
      ? svgText
      : svgText.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');

    const encoded = encodeURIComponent(safeText)
      .replace(/'/g, "%27")
      .replace(/"/g, "%22");

    const dataUrl = `data:image/svg+xml;charset=utf-8,${encoded}`;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = dataUrl;
    await img.decode();
    return img;
  }
  /* =======================
     END YOURAPP: Image loading helpers
     ======================= */

  /* =========================
     BEGIN YOURAPP: Modal builder (10vh margins + scroll)
     ========================= */
  function openModal() {
    const wrap = document.createElement("div");
    wrap.innerHTML = `
      <div class="lm-backdrop" style="position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:99999;display:flex;align-items:flex-start;justify-content:center;padding:10vh 16px;">
        <div class="lm-modal" style="background:#fff;border-radius:12px;max-width:980px;width:100%;padding:14px;box-shadow:0 10px 35px rgba(0,0,0,.25);max-height:80vh;overflow:auto;">
          <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;">
            <strong id="lm-title"></strong>
            <button type="button" data-close style="font-size:18px;line-height:1;border:0;background:transparent;cursor:pointer;">✕</button>
          </div>

          <div id="lm-stage" style="width:100%;height:52vh;margin-top:12px;border:1px solid #ddd;border-radius:10px;overflow:hidden;"></div>

          <div id="lm-thumbs" style="display:flex;gap:8px;overflow:auto;margin-top:10px;padding-bottom:6px;"></div>

          <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-top:12px;">
            <input type="file" id="lm-file" accept=".ai,.eps,.svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg" />
            <button type="button" id="lm-removebg"></button>
            <button type="button" id="lm-add"></button>
            <span id="lm-msg" style="opacity:.75;font-size:13px;"></span>
          </div>

          <div id="lm-bg-slider" style="display:flex;flex-direction:column;gap:4px;margin-top:10px;">
            <div style="display:flex;justify-content:space-between;gap:12px;align-items:center;">
              <label for="lm-tol" id="lm-tol-label" style="font-size:13px;opacity:.85;"></label>
              <span id="lm-tol-val" style="font-size:13px;opacity:.85;"></span>
            </div>
            <input type="range" id="lm-tol" min="10" max="70" step="1" value="30">
            <small id="lm-tol-help" style="opacity:.65;"></small>
          </div>

          <div style="margin-top:10px;">
            <button type="button" id="lm-aieps-help-link"
              style="border:0;background:transparent;padding:0;color:inherit;text-decoration:underline;cursor:pointer;font-size:13px;opacity:.9;">
            </button>

            <div id="lm-aieps-help-panel"
              style="display:none;margin-top:8px;border:1px solid #e5e5e5;border-radius:10px;padding:10px;background:#fafafa;">
              <div style="display:flex;justify-content:space-between;gap:12px;align-items:center;">
                <strong id="lm-aieps-help-title" style="font-size:13px;"></strong>
                <button type="button" id="lm-aieps-help-close"
                  style="border:0;background:transparent;cursor:pointer;font-size:16px;line-height:1;">✕</button>
              </div>
              <pre id="lm-aieps-help-body"
                style="white-space:pre-wrap;margin:8px 0 0 0;font-size:12.5px;line-height:1.35;opacity:.9;"></pre>
            </div>
          </div>

          <div id="lm-zone-tools" style="display:none;gap:10px;align-items:center;margin-top:10px;flex-wrap:wrap;">
            <label style="display:flex;gap:6px;align-items:center;">
              <input type="checkbox" id="lm-zone-toggle">
              <span id="lm-zone-label"></span>
            </label>
            <button type="button" id="lm-zone-copy"></button>
            <input type="text" id="lm-zone-out" readonly style="flex:1;min-width:260px;" />
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(wrap);

    wrap.querySelector("[data-close]").onclick = () => wrap.remove();
    wrap.querySelector(".lm-backdrop").addEventListener("click", (e) => {
      if (e.target.classList.contains("lm-backdrop")) wrap.remove();
    });

    wrap.querySelector("#lm-title").textContent = t("title");
    wrap.querySelector("#lm-removebg").textContent = t("removeBg");
    wrap.querySelector("#lm-add").textContent = t("addToCart");

    wrap.querySelector("#lm-tol-label").textContent = t("bgSensitivity");
    wrap.querySelector("#lm-tol-help").textContent = t("bgSensitivityHelp");

    wrap.querySelector("#lm-aieps-help-link").textContent = t("aiEpsHelpLink");
    wrap.querySelector("#lm-aieps-help-title").textContent = t("aiEpsHelpTitle");
    wrap.querySelector("#lm-aieps-help-body").textContent = t("aiEpsHelpBody");

    if (zoneEditorAllowed) {
      const zoneTools = wrap.querySelector("#lm-zone-tools");
      zoneTools.style.display = "flex";
      wrap.querySelector("#lm-zone-label").textContent = t("zoneEdit");
      wrap.querySelector("#lm-zone-copy").textContent = t("copyZone");
    }

    return wrap;
  }

  function setMsg(modal, text) {
    const el = modal.querySelector("#lm-msg");
    if (el) el.textContent = text || "";
  }
  /* =======================
     END YOURAPP: Modal builder
     ======================= */

  /* =========================
     BEGIN YOURAPP: Konva clamp helper
     ========================= */
  function clampNodeIntoRect(node, rectPx) {
    const box = node.getClientRect({ skipTransform: false });
    let dx = 0, dy = 0;

    if (box.x < rectPx.x) dx = rectPx.x - box.x;
    if (box.y < rectPx.y) dy = rectPx.y - box.y;
    if (box.x + box.width > rectPx.x + rectPx.width) dx = (rectPx.x + rectPx.width) - (box.x + box.width);
    if (box.y + box.height > rectPx.y + rectPx.height) dy = (rectPx.y + rectPx.height) - (box.y + box.height);

    if (dx || dy) node.position({ x: node.x() + dx, y: node.y() + dy });
  }
  /* =======================
     END YOURAPP: Konva clamp helper
     ======================= */

  /* =========================
     BEGIN YOURAPP: Main editor flow
     ========================= */
  async function runEditor() {
    if (!variantId) return;
    if (!apiBase) return;
    if (!Array.isArray(images) || images.length === 0) return;

    const modal = openModal();
    const stageEl = modal.querySelector("#lm-stage");
    const thumbsEl = modal.querySelector("#lm-thumbs");

    const tolInput = modal.querySelector("#lm-tol");
    const tolVal = modal.querySelector("#lm-tol-val");
    function updateTolUI() {
      const v = Number(tolInput?.value || 30);
      if (tolVal) tolVal.textContent = String(v);
    }
    updateTolUI();
    tolInput?.addEventListener("input", updateTolUI);

    const helpPanel = modal.querySelector("#lm-aieps-help-panel");
    const helpClose = modal.querySelector("#lm-aieps-help-close");
    function openHelp() { if (helpPanel) helpPanel.style.display = "block"; }
    function closeHelp() { if (helpPanel) helpPanel.style.display = "none"; }
    modal.querySelector("#lm-aieps-help-link")?.addEventListener("click", () => {
      if (!helpPanel) return;
      helpPanel.style.display = helpPanel.style.display === "block" ? "none" : "block";
    });
    helpClose?.addEventListener("click", closeHelp);

    const W = stageEl.clientWidth;
    const H = stageEl.clientHeight;

    const stage = new Konva.Stage({ container: stageEl, width: W, height: H });
    const layer = new Konva.Layer();
    stage.add(layer);

    const zoneRel = { ...zoneRelDefault };
    const zonePx = { x: zoneRel.x * W, y: zoneRel.y * H, width: zoneRel.w * W, height: zoneRel.h * H };

    /* --------------------------------
       BEGIN YOURAPP: Robust background load (fallback to thumb)
       -------------------------------- */
    let currentBg = images[0]?.src || images[0]?.thumb;
    setMsg(modal, t("loadingImg"));

    let bgImg = null;
    try {
      bgImg = await loadImage(currentBg);
    } catch (err) {
      const fallback = images[0]?.thumb || images[0]?.src;
      currentBg = fallback;
      try {
        bgImg = await loadImage(fallback);
      } catch (err2) {
        setMsg(modal, isEN ? "Unable to load product image." : "Impossible de charger l’image produit.");
        console.warn("[logo-mockup] background load failed", err2);
        return;
      }
    }

    const bg = new Konva.Image({ image: bgImg, x: 0, y: 0, width: W, height: H, listening: false });
    layer.add(bg);
    /* --------------------------------
       END YOURAPP: Robust background load (fallback to thumb)
       -------------------------------- */

    const zoneDisplayRect = new Konva.Rect({
      ...zonePx,
      stroke: "#666",
      dash: [8, 6],
      listening: false
    });
    layer.add(zoneDisplayRect);

    let logoNode = null;
    let logoTransformer = null;
    let currentLogoFile = null;

    function attachLogoTransformer(node) {
      if (logoTransformer) logoTransformer.destroy();
      logoTransformer = new Konva.Transformer({
        nodes: [node],
        rotateEnabled: false,
        keepRatio: true,
        enabledAnchors: ["top-left", "top-right", "bottom-left", "bottom-right"]
      });
      layer.add(logoTransformer);
    }

    async function setLogoFromFile(file) {
      const url = URL.createObjectURL(file);
      const img = await loadImage(url);
      currentLogoFile = file;

      if (logoNode) logoNode.destroy();

      const targetW = zonePx.width * 0.6;
      const scale = targetW / img.width;

      logoNode = new Konva.Image({
        image: img,
        x: zonePx.x + zonePx.width * 0.2,
        y: zonePx.y + zonePx.height * 0.2,
        draggable: true,
        scaleX: scale,
        scaleY: scale
      });

      logoNode.on("dragmove transform", () => {
        clampNodeIntoRect(logoNode, zonePx);
        layer.batchDraw();
      });

      layer.add(logoNode);
      attachLogoTransformer(logoNode);
      layer.draw();
    }

    async function setLogoFromHTMLImage(img, fileForUpload) {
      currentLogoFile = fileForUpload;

      if (logoNode) logoNode.destroy();

      const targetW = zonePx.width * 0.6;
      const scale = targetW / img.width;

      logoNode = new Konva.Image({
        image: img,
        x: zonePx.x + zonePx.width * 0.2,
        y: zonePx.y + zonePx.height * 0.2,
        draggable: true,
        scaleX: scale,
        scaleY: scale
      });

      logoNode.on("dragmove transform", () => {
        clampNodeIntoRect(logoNode, zonePx);
        layer.batchDraw();
      });

      layer.add(logoNode);
      attachLogoTransformer(logoNode);
      layer.draw();
    }

    /* --------------------------------
       BEGIN YOURAPP: Thumbnails to switch background image (robust fallback)
       -------------------------------- */
    thumbsEl.innerHTML = images.map((it) => `
      <button type="button" data-bg="${it.src || ""}"
        style="border:1px solid #ddd;border-radius:10px;padding:0;overflow:hidden;flex:0 0 auto;background:#fff;cursor:pointer;">
        <img src="${it.thumb || it.src || ""}" alt="" style="display:block;width:72px;height:72px;object-fit:cover;">
      </button>
    `).join("");

    thumbsEl.addEventListener("click", async (e) => {
      const btn = e.target.closest("button");
      if (!btn) return;

      try {
        setMsg(modal, t("changeImg"));
        const desired = btn.dataset.bg || "";
        currentBg = desired;

        try {
          const newImg = await loadImage(desired);
          bg.image(newImg);
        } catch {
          const thumb = btn.querySelector("img")?.src;
          if (!thumb) throw new Error("no thumb");
          const newImg = await loadImage(thumb);
          bg.image(newImg);
          currentBg = thumb;
        }

        layer.batchDraw();
        setMsg(modal, "");
      } catch {
        setMsg(modal, "");
      }
    });
    /* --------------------------------
       END YOURAPP: Thumbnails to switch background image
       -------------------------------- */

    const dpoInput = document.querySelector(dpoSelector);
    if (dpoInput?.value && /^https?:\/\//.test(dpoInput.value)) {
      try {
        setMsg(modal, t("loadingLogo"));
        const blob = await fetchBlob(dpoInput.value);
        const mime = blob.type || "";
        if (mime === "image/svg+xml") {
          const svgFile = fileFromBlob(blob, "logo-from-dpo.svg");
          const img = await svgFileToHTMLImage(svgFile);
          await setLogoFromHTMLImage(img, svgFile);
        } else {
          const f = fileFromBlob(blob, "logo-from-dpo");
          await setLogoFromFile(f);
        }
        setMsg(modal, "");
      } catch {
        setMsg(modal, t("uploadHint"));
      }
    } else {
      setMsg(modal, t("uploadHint"));
    }

    modal.querySelector("#lm-file").addEventListener("change", async (ev) => {
      const f = ev.target.files?.[0];
      if (!f) return;

      const ext = extOf(f.name);

      if (isVectorPrintExt(ext)) {
        currentLogoFile = f;
        setMsg(modal, t("aiEpsNotPreview"));
        openHelp();
        return;
      }

      if (isSvgExt(ext) || f.type === "image/svg+xml") {
        try {
          setMsg(modal, t("loadingLogo"));
          const img = await svgFileToHTMLImage(f);
          await setLogoFromHTMLImage(img, f);
          setMsg(modal, "");
        } catch {
          setMsg(modal, t("unsupported"));
        }
        return;
      }

      if (isRasterExt(ext) || f.type === "image/png" || f.type === "image/jpeg") {
        setMsg(modal, t("loadingLogo"));
        await setLogoFromFile(f);
        setMsg(modal, "");
        return;
      }

      setMsg(modal, t("unsupported"));
    });

    modal.querySelector("#lm-removebg").onclick = async () => {
      if (!currentLogoFile) return setMsg(modal, t("needLogo"));

      const ext = extOf(currentLogoFile.name);
      if (!isRasterExt(ext)) return setMsg(modal, t("svgNoBg"));

      try {
        const tol = Number(modal.querySelector("#lm-tol")?.value || 30);
        setMsg(modal, t("removingBg"));

        const outBlob = await removeBackgroundLocal(currentLogoFile, {
          tolerance: tol,
          feather: 1,
          maxSize: 1600
        });

        const outFile = new File([outBlob], "logo-nobg.png", { type: "image/png" });
        await setLogoFromFile(outFile);

        setMsg(modal, t("removedBgOk"));
        setTimeout(() => setMsg(modal, ""), 1200);
      } catch {
        setMsg(modal, t("errBg"));
      }
    };

    if (zoneEditorAllowed) {
      zoneDisplayRect.visible(false);

      const zoneTools = modal.querySelector("#lm-zone-tools");
      const zoneToggle = modal.querySelector("#lm-zone-toggle");
      const zoneCopyBtn = modal.querySelector("#lm-zone-copy");
      const zoneOut = modal.querySelector("#lm-zone-out");

      const zoneEditRect = new Konva.Rect({
        x: zonePx.x,
        y: zonePx.y,
        width: zonePx.width,
        height: zonePx.height,
        stroke: "#111",
        dash: [8, 6],
        draggable: false
      });
      layer.add(zoneEditRect);

      const zoneTr = new Konva.Transformer({
        nodes: [zoneEditRect],
        rotateEnabled: false,
        keepRatio: false,
        enabledAnchors: ["top-left", "top-right", "bottom-left", "bottom-right"]
      });
      zoneTr.visible(false);
      layer.add(zoneTr);

      function bakeScale(rect) {
        const wPx = rect.width() * rect.scaleX();
        const hPx = rect.height() * rect.scaleY();
        rect.width(wPx);
        rect.height(hPx);
        rect.scale({ x: 1, y: 1 });
      }

      function clampZoneToStage(rect) {
        bakeScale(rect);
        let x = rect.x();
        let y = rect.y();
        const w = rect.width();
        const h = rect.height();

        if (x < 0) x = 0;
        if (y < 0) y = 0;
        if (x + w > W) x = W - w;
        if (y + h > H) y = H - h;

        rect.position({ x, y });
      }

      function toRelFromRect(rect) {
        return {
          x: +(rect.x() / W).toFixed(4),
          y: +(rect.y() / H).toFixed(4),
          w: +(rect.width() / W).toFixed(4),
          h: +(rect.height() / H).toFixed(4)
        };
      }

      function syncZone() {
        const rel = toRelFromRect(zoneEditRect);
        zoneOut.value = JSON.stringify({ version: 1, zone: rel });

        zoneRel.x = rel.x; zoneRel.y = rel.y; zoneRel.w = rel.w; zoneRel.h = rel.h;
        zonePx.x = zoneRel.x * W;
        zonePx.y = zoneRel.y * H;
        zonePx.width = zoneRel.w * W;
        zonePx.height = zoneRel.h * H;

        if (logoNode) clampNodeIntoRect(logoNode, zonePx);
      }

      function setZoneEditing(enabled) {
        zoneEditRect.draggable(enabled);
        zoneTr.visible(enabled);
        if (logoNode) logoNode.draggable(!enabled);
        if (logoTransformer) logoTransformer.visible(!enabled);
        layer.batchDraw();
      }

      syncZone();
      zoneToggle.checked = false;
      zoneToggle.addEventListener("change", () => setZoneEditing(zoneToggle.checked));

      zoneEditRect.on("dragmove transform", () => {
        clampZoneToStage(zoneEditRect);
        syncZone();
        layer.batchDraw();
      });

      zoneCopyBtn.addEventListener("click", async () => {
        const json = zoneOut.value;
        try {
          await navigator.clipboard.writeText(json);
          setMsg(modal, t("copied"));
          setTimeout(() => setMsg(modal, ""), 1200);
        } catch {
          zoneOut.focus();
          zoneOut.select();
          setMsg(modal, t("manualCopy"));
        }
      });

      zoneTools.style.display = "flex";
    }

    modal.querySelector("#lm-add").onclick = async () => {
      if (!logoNode) return setMsg(modal, t("previewRequired"));
      if (!currentLogoFile) return setMsg(modal, t("needLogo"));

      try {
        setMsg(modal, t("rendering"));

        const dataUrl = stage.toDataURL({ pixelRatio: 2 });
        const mockupBlob = await (await fetch(dataUrl)).blob();
        const mockupFile = fileFromBlob(mockupBlob, "mockup.png");

        setMsg(modal, t("uploading"));

        const [logoUp, mockupUp] = await Promise.all([
          uploadToWorker(currentLogoFile, "logo"),
          uploadToWorker(mockupFile, "mockup")
        ]);

        const transform = {
          bg: currentBg,
          zone: { ...zoneRel },
          logo: {
            x: +(logoNode.x() / W).toFixed(4),
            y: +(logoNode.y() / H).toFixed(4),
            scaleX: +logoNode.scaleX().toFixed(4),
            scaleY: +logoNode.scaleY().toFixed(4)
          }
        };

        setMsg(modal, t("adding"));

        await addToCartWithProps({
          "_mockup_url": mockupUp.url,
          "_logo_url": logoUp.url,
          "_mockup_transform": JSON.stringify(transform)
        });

        modal.remove();
      } catch {
        setMsg(modal, t("errAdd"));
      }
    };

    setMsg(modal, "");
    layer.draw();
  }
  /* =======================
     END YOURAPP: Main editor flow
     ======================= */

  /* =========================
     BEGIN YOURAPP: Button click binding
     ========================= */
  document.addEventListener("click", (e) => {
    if (!e.target.closest("#logo-mockup-open")) return;
    runEditor();
  });
  /* =======================
     END YOURAPP: Button click binding
     ======================= */
})();


/* BEGIN YOURAPP: debug */
console.log("[logo-mockup] loaded", { hasKonva: !!window.Konva, hasRoot: !!document.getElementById("logo-mockup-root") });
document.addEventListener("click", (e) => {
  if (e.target.closest("#logo-mockup-open")) console.log("[logo-mockup] open clicked");
});
/* END YOURAPP: debug */